import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { hookstate, useHookstate } from '@hookstate/core';

import { Tooltip, TooltipProvider, TooltipWrapper } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

import Slider from 'react-slick';
import parse from 'html-react-parser';
import Cookies from 'js-cookie';
import ShareOnSocial from 'react-share-on-social';

import Chord from '../Chord/index.js';
import '../Chord/styles.css';
import Piano from '../Piano/index.js';
import * as PianoChordUtility from '../PianoChordUtility.js';

import { renderToString } from 'react-dom/server';
import JSZip from 'jszip';

import { StrummingDisplay, StrummingEditor } from '../StrummingPattern/index.jsx';

const mappedComponents = {}

export const registerComponent = (id, component) => {
	// add optional checks
	mappedComponents[id] = component;
}

export const getComponentById = id => {
	// add optional checks
	if(id) {
		return mappedComponents[id] || null
	}
	return null;
}

export const parseJsonProps = JsonProps => {
	try {
		return JSON.parse(JsonProps);
	} catch {
		return {};
	}
}

const globalState = hookstate({
	shift:              chords_settings.transpose,
	font_size:          chords_settings.font_size,
	show_chord_tooltip: chords_settings.show_chord_tooltip == 'true',
	show_chords:        chords_settings.show_chords == 'true',
	column_count:       force_chords_column_count !== null ? force_chords_column_count : chords_settings.column_count,
	simplify_chords:    chords_settings.simplify_chords == 'true',
});
const chord_order = [ 'C', 'C#', 'D', 'Es', 'E', 'F', 'F#', 'G', 'G#', 'A', 'B', 'H' ];

// when simplifying, use the simplified chord's own key/suffix (and diagram) instead of the original chord's
const resolveChordEntry = (c, simplify) => {
	const entry = chord_db[c];
	if (!entry) return entry;

	if (simplify && entry.simple_key && chord_db[entry.simple_key]) {
		return chord_db[entry.simple_key];
	}

	return entry;
};

const getChordVariations = (chordTitle, fallbackKey, instrument) => {
	if (instrument === 'piano') {
		const chord = PianoChordUtility.parseChord(chordTitle);
		return chord ? PianoChordUtility.getVoicings(chord) : [];
	}

	const chord_positions = (chord_db[chordTitle] ?? chord_db[fallbackKey]).positions;
	return chord_positions.slice().sort((a, b) => {
		const val = a.baseFret - b.baseFret;
		return val == 0 ? a.id - b.id : val;
	});
};

const ChordShape = ({ variation, instrument, lite }) => {
	if (!variation) return null;

	if (instrument === 'piano') {
		return <Piano voicing={variation} lite={lite} />;
	}
	return <Chord chord={variation} instrument={getInstrument(instrument)} lite={lite} />;
};

/*registerComponent('ChordButton', () => {
	const state = useHookstate(globalState);
	
	return <button onClick={() => {
		state.set(p => { return {...p, shift: p.shift + 1 }; });
	}}>click me</button>;
});
registerComponent('ChordText', () => {
	const state = useHookstate(globalState);
	
	return <div>shift by: {state.get().shift}</div>;
});
const fontSizeComponent = () => {

};*/
const requireLogin = () => {
	if (window.$) window.$('#errorRegisterRequired').modal('show');
};

const FontSize = props => {
	const state = useHookstate(globalState);

	if (props.style !== undefined && props.style == 'print') {
		return <>
			<button className='default' onClick={() => {
				if (navigator.vibrate) navigator.vibrate(50);
				state.set(s => {
					const font_size = Math.max(0.2, s.font_size - 0.1);
					saveChordsSettings(chords_settings.font_size_key, font_size);
					
					return {...s, font_size };
				});
			}} style={{ marginRight: 4 }}>
				<i className='fa-light fa-minus'></i>
			</button>
			{props.title === undefined ? null : <>
				<span className='show-for-desktop'>{ props.title }</span>
				<span className='show-for-mobile'><i className='fa-regular fa-text-size'></i></span>
			</>}
			<button className='default' onClick={() => {
				if (navigator.vibrate) navigator.vibrate(50);
				state.set(s => {
					const font_size = Math.min(3, s.font_size + 0.1);
					saveChordsSettings(chords_settings.font_size_key, font_size);
					
					return {...s, font_size };
				});
			}}>
				<i className='fa-light fa-plus'></i>
			</button>
		</>
	}
	
	// 1 is starting value, 0.1 is step size
	const relative_font_size = Math.round((state.get().font_size - 1) / 0.1);
	
	return <div className='section'>
		<div className='top'>
			<a onClick={() => {
				if (props.loggedin === false) { requireLogin(); return; }
				state.set(s => {
					const font_size = Math.max(0.2, s.font_size - 0.1);
					saveChordsSettings(chords_settings.font_size_key, font_size);

					return {...s, font_size };
				});
			}}>
				<i className='fa-thin fa-circle-minus'></i>
			</a>
			<div className='desktop-view'>
				<span className='middle'>{
					(relative_font_size > 0 ? '+' : '') + relative_font_size
				}</span>
				<label className='name-middle'>{ props.trans.font_size }</label>
			</div>
			<a onClick={() => {
				if (props.loggedin === false) { requireLogin(); return; }
				state.set(s => {
					const font_size = Math.min(3, s.font_size + 0.1);
					saveChordsSettings(chords_settings.font_size_key, font_size);

					return {...s, font_size }
				});
			}}>
				<i className='fa-thin fa-circle-plus'></i>
			</a>
		</div>
		<label className='name'>{ props.trans.font_size }</label>
	</div>;
};
registerComponent('FontSize', FontSize);

const Transpose = props => {
	const state = useHookstate(globalState);
	
	if (props.style !== undefined && props.style == 'print') {
		return <>
			<a onClick={() => {
				state.set(p => {
					const current_shift = p.shift[props.transpose_key] || 0;
					
					let s = current_shift - 1;
					if (s < 0) s += chord_order.length;
					
					const shift = s % chord_order.length;
					
					const new_state = { ...p.shift };
					new_state[props.transpose_key] = shift;
					
					saveChordsSettings('transpose_shift', new_state);
					
					return {...p, shift: new_state };
				});
			}}><i className='fa-light fa-square-minus'></i></a>
			
			<label><span>{ props.trans.transpone }&nbsp;</span><span style={{ fontSize: '0.6em', verticalAlign: 'super' }}>{
				(state.get().shift[props.transpose_key] || 0) == 0 ? '' : state.get().shift[props.transpose_key]
			}</span></label>
			
			<a onClick={() => {
				state.set(p => {
					const current_shift = p.shift[props.transpose_key] || 0;
					
					const shift = (current_shift + 1) % chord_order.length;
					
					const new_state = { ...p.shift };
					new_state[props.transpose_key] = shift;
					
					saveChordsSettings('transpose_shift', new_state);
					
					return {...p, shift: new_state };
				});
			}}><i className='fa-light fa-square-plus'></i></a>
		</>;
	}
	return <div className='section'>
		<div className='top'>
			<a onClick={() => {
				if (props.loggedin === false) { requireLogin(); return; }
				if (navigator.vibrate) navigator.vibrate(50);
				state.set(p => {
					const current_shift = p.shift[props.transpose_key] || 0;

					let s = current_shift - 1;
					if (s < 0) s += chord_order.length;

					const shift = s % chord_order.length;

					const new_state = { ...p.shift };
					new_state[props.transpose_key] = shift;

					saveChordsSettings('transpose_shift', new_state);

					return {...p, shift: new_state };
				});
			}}>
				<i className='fa-thin fa-circle-minus'></i>
			</a>
			<div className='desktop-view'>
				<span  className='middle'>{
					(state.get().shift[props.transpose_key] || 0 > 0 ? '+' : '') +
					(state.get().shift[props.transpose_key] || 0)
				}</span>
				<label className='name-middle'>{ props.trans.transponing }</label>
			</div>
			<a onClick={() => {
				if (props.loggedin === false) { requireLogin(); return; }
				if (navigator.vibrate) navigator.vibrate(50);
				state.set(p => {
					const current_shift = p.shift[props.transpose_key] || 0;

					const shift = (current_shift + 1) % chord_order.length;

					const new_state = { ...p.shift };
					new_state[props.transpose_key] = shift;

					saveChordsSettings('transpose_shift', new_state);

					return {...p, shift: new_state };
				});
			}}>
				<i className='fa-thin fa-circle-plus'></i>
			</a>
		</div>
		<label className='name'>{ props.trans.transponing }</label>
	</div>
};
registerComponent('Transpose', Transpose);

const ChordTooltipToggle = props => {
	const state = useHookstate(globalState);
	
	if (props.style !== undefined && props.style == 'mobile-popup') {
		return <div className='sign__group sign__group--checkbox'>
			<input
				id='remember-chords-tooltips'
				type='checkbox'
				checked={state.get().show_chord_tooltip}
				onChange={e => {
					state.set(s => { return {...s, show_chord_tooltip: e.target.checked }});
					saveChordsSettings('show_chord_tooltip', e.target.checked ? 'true' : 'false');
				}} />
			<label htmlFor='remember-chords-tooltips'>{ props.trans.show_chords_in_lyrics }</label>
		</div>;
	}
	return <div className='custom-control custom-switch'>
		<input
			type='checkbox'
			className='custom-control-input'
			id='showChords'
			checked={state.get().show_chord_tooltip}
			onChange={e => {
				state.set(s => { return {...s, show_chord_tooltip: e.target.checked }});
				saveChordsSettings('show_chord_tooltip', e.target.checked ? 'true' : 'false');
			}} />
		<label className='custom-control-label' htmlFor='showChords'>{ props.trans.chords_in_lyrics }</label>
	</div>;
};
registerComponent('ChordTooltipToggle', ChordTooltipToggle);

const featureLockBadge = <span className='feature-lock'><svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><rect x='5' y='11' width='14' height='10' rx='2.5' /><path d='M8 11V7a4 4 0 0 1 8 0v4' /><circle cx='12' cy='15.3' r='1.2' fill='currentColor' stroke='none' /><path d='M12 16.6v1.6' /></svg></span>;

// props.locked: not subscribed - toggle stays visible with a lock badge, opens the paywall instead
const SimplifyChordsToggle = props => {
	const state = useHookstate(globalState);

	const handleChange = e => {
		if (props.locked) {
			if (window.$) window.$('#simplifyPaywall').modal('show');
			return;
		}
		state.set(s => { return {...s, simplify_chords: e.target.checked }});
		saveChordsSettings('simplify_chords', e.target.checked ? 'true' : 'false');
	};

	if (props.style !== undefined && props.style == 'mobile-popup') {
		return <div className='sign__group sign__group--checkbox'>
			<input
				id='remember-simplify-chords'
				type='checkbox'
				checked={!props.locked && state.get().simplify_chords}
				onChange={handleChange} />
			<label htmlFor='remember-simplify-chords'>{ props.trans.simplify_chords }{ props.locked && featureLockBadge }</label>
		</div>;
	}
	return <div className='custom-control custom-switch'>
		<input
			type='checkbox'
			className='custom-control-input'
			id='simplifyChords'
			checked={!props.locked && state.get().simplify_chords}
			onChange={handleChange} />
		<label className='custom-control-label' htmlFor='simplifyChords'>{ props.trans.simplify_chords }{ props.locked && featureLockBadge }</label>
	</div>;
};
registerComponent('SimplifyChordsToggle', SimplifyChordsToggle);

const ChordsToggle = props => {
	const state = useHookstate(globalState);
	
	const [ show_chords, setShowChords ] = useState(state.get().show_chords);
	
	// we're delaying doing anything of consequence, so that animation can finish (otherwise animations are choppy)
	const animation_duration_ms = 300;
	useEffect(() => {
		const id = setTimeout(() => {
			state.set(s => { return {...s, show_chords }});
			saveChordsSettings('show_chords', show_chords ? 'true' : 'false');
		}, animation_duration_ms);
		return () => clearTimeout(id);
	}, [ show_chords ]);
	
	if (props.style !== undefined && props.style == 'print') {
		return <>
			<input
				style={{ display: 'none' }}
				type='checkbox'
				id='chordsImages'
				checked={show_chords}
				onChange={e => setShowChords(e.target.checked)}  />
			<label className='switch' htmlFor='chordsImages'></label>
		</>;
	}
	else if (props.style !== undefined && props.style == 'mobile-popup') {
		return <div className='sign__group sign__group--checkbox'>
			<input
				id='remember-chords'
				type='checkbox'
				checked={show_chords}
				onChange={e => setShowChords(e.target.checked)}  />
			<label htmlFor='remember-chords'>{ props.trans.chords_on_top }</label>
		</div>;
	}
	return <div className='custom-control custom-switch'>
		<input
			type='checkbox'
			className='custom-control-input'
			id='chordsImages'
			checked={show_chords}
			onChange={e => setShowChords(e.target.checked)}  />
		<label className='custom-control-label' htmlFor='chordsImages'>{ props.trans.chords }</label>
	</div>;
};
registerComponent('ChordsToggle', ChordsToggle);

const ColumnCountToggle = props => {
	const state = useHookstate(globalState);
	
	const [ column_count, setColumnCount ] = useState(state.get().column_count);
	
	// we're delaying doing anything of consequence, so that animation can finish (otherwise animations are choppy)
	const animation_duration_ms = 300;
	useEffect(() => {
		const id = setTimeout(() => {
			state.set(s => { return {...s, column_count }});
			saveChordsSettings('chords_column_count', column_count);
		}, animation_duration_ms);
		return () => clearTimeout(id);
	}, [ column_count ]);
	
	if (props.style !== undefined && props.style == 'print') {
		return <>
			<input
				style={{ display: 'none' }}
				type='checkbox'
				id='columnCountSwitch'
				checked={column_count == 2}
				onChange={e => setColumnCount(e.target.checked ? 2 : 1)} />
			<label className='switch' htmlFor='columnCountSwitch'></label>
		</>;
	}
	return <div className='custom-control custom-switch'>
		<input
			type='checkbox'
			className='custom-control-input'
			id='columnCountSwitch'
			checked={column_count == 2}
			onChange={e => setColumnCount(e.target.checked ? 2 : 1)} />
		<label className='custom-control-label' htmlFor='columnCountSwitch'>{ props.trans.two_columns }</label>
	</div>;
};
registerComponent('ColumnCountToggle', ColumnCountToggle);

const Chords = props => {
	const state = useHookstate(globalState);
	
	if (!state.get().show_chords) {
		return <div></div>;
	}

	const current_shift = state.get().shift[props.transpose_key] || 0;
	const simplify       = state.get().simplify_chords;

	// simplify can map multiple distinct original chords onto the same displayed chord, so dedupe on the resolved title
	const seen_titles = new Set();
	const chord_boxes = [];

	for (const c of props.chords) {
		const chord_entry = resolveChordEntry(c, simplify);

		const chord_key         = chord_entry.chord_key;
		const shift             = (chord_order.indexOf(chord_key) + current_shift) % chord_order.length;
		const chord_key_shifted = chord_order[shift] + chord_entry.chord_suffix;

		const chord_key_2         = chord_entry.chord_key_2;
		const shift_2             = chord_key_2.length == 0 ? 0 : ((chord_order.indexOf(chord_key_2) + current_shift) % chord_order.length);
		const chord_key_2_shifted = (chord_key_2.length == 0 ? '' : chord_order[shift_2]) + chord_entry.chord_suffix_2;

		let chord_title = chord_key_shifted;
		if (chord_key_2_shifted.length > 0) {
			chord_title += '/' + chord_key_2_shifted;
		}

		if (seen_titles.has(chord_title)) continue;
		seen_titles.add(chord_title);

		const variation = getChordVariations(chord_title, c, props.instrument)[0];

		chord_boxes.push(<div className='chord' key={'chord-' + chord_title}>
			<div className='chord-title'>{chord_title}</div>
			<div className={'chord-react' + (props.instrument === 'piano' ? '' : ' auto-color-scheme')}>
				<ChordShape
					variation={variation}
					instrument={props.instrument}
					lite={false} />
			</div>
		</div>);
	}

	return <div className={'chords-container ' + props.instrument}>
		{chord_boxes}
	</div>;
};
registerComponent('Chords', Chords);

const ChordsUtility = props => {
	const state = useHookstate(globalState);

	let instrument = props.instrument;
	if (instrument === null || instrument === undefined || instrument == '') {
		instrument = 'guitar';
	}

	const chord_key_order    = [ 'C', 'C#', 'D', 'Es', 'E', 'F', 'G', 'G#', 'A', 'B', 'H' ];
	const chord_suffix_order = [ 'major', 'm', '7', 'm7', 'maj7', 'sus2', 'sus4', '°', '°7', '+', '+7', 'alt', 'alt7' ];

	const chords_keyed     = {};
	const chord_variations = {};

	if (instrument === 'piano') {
		const piano_suffix_order = chord_suffix_order.filter(suffix => suffix !== 'alt7');

		for (const key of chord_order) {
			chords_keyed[key] = piano_suffix_order.slice();

			for (const suffix of piano_suffix_order) {
				const formula_suffix = suffix === 'major' ? '' : suffix;
				const chord = PianoChordUtility.parseChord(key + formula_suffix);

				chord_variations[key + '--' + suffix] = chord ? PianoChordUtility.getVoicings(chord) : [];
			}
		}
	} else {
		for (let key in chord_db) {
			const chord = chord_db[key];
			if (chord.positions.length > 0) {
				let chord_key = chord.chord_key;
				if (chord.chord_key_2.length > 0) chord_key += '/' + chord.chord_key_2;

				if (chord_key.length == 0) continue;

				let chord_suffix = chord.chord_suffix;
				if (chord.chord_suffix_2.length > 0) chord_suffix += chord.chord_suffix_2;

				if (chords_keyed[chord_key] === undefined) {
					chords_keyed[chord_key] = [];
				}

				const suffix = chord_suffix == '' ? 'major' : chord_suffix;
				if (chords_keyed[chord_key].indexOf(suffix) === -1) {
					chords_keyed[chord_key].push(suffix);
				}

				chord_variations[chord_key + '--' + suffix] = chord.positions;
			}
		}
	}

	const sorted_chords = Object.keys(chords_keyed).sort((a, b) => {
		const ord_a = chord_key_order.indexOf(a) === -1 ? 9999 : chord_key_order.indexOf(a);
		const ord_b = chord_key_order.indexOf(b) === -1 ? 9999 : chord_key_order.indexOf(b);

		return ord_a - ord_b;
	});

	const [ selectedChordKey,    setSelectedChordKey    ] = useState(sorted_chords[0]);
	const [ selectedChordSuffix, setSelectedChordSuffix ] = useState(chords_keyed[sorted_chords[0]][0]);

	const translateSuffix = suffix => {
		if      (suffix == 'major') return 'dur';
		else if (suffix ==     'm') return 'mol';
		else if (suffix ==     '°') return 'dim';
		else if (suffix ==     '+') return 'aug';
		
		return suffix;
	};
	
	let title = selectedChordKey + ' ' + translateSuffix(selectedChordSuffix);
	
	return <div className='sidebar-chords'>
		<div className='akordi-wrap'>
			<div className='dur'>{title}</div>
			<div className='choices'>
				<div>
					<label className='mt-2'>{ props.trans.instrument }</label>
					<select className='minimal' defaultValue={instrument} onChange={event => {
						document.location = 'moj-racun/pripomocki.html?v=akordi&instrument=' + event.target.value;
					}}>
						<option value='guitar'>{ props.trans.guitar }</option>
						<option value='ukulele'>{ props.trans.ukulele }</option>
						<option value='piano'>{ props.trans.piano }</option>
					</select>
				</div>
				<div>
					<label className='mt-2'>{ props.trans.key }</label>
					<select
						name='chords-key'
						id='chords-key'
						className='minimal'
						onChange={e => {
							setSelectedChordKey(e.target.value);
							setSelectedChordSuffix(chords_keyed[e.target.value][0]);
						}}>
						{sorted_chords.map(key => <option
							key={'key-' + key}
							value={key}
							checked={key == selectedChordKey}>
							{key}
						</option>)}
					</select>
				</div>
				<div>
					<label className='mt-2'>{ props.trans.scales }</label>
					<select
						name='chords-chart'
						id='chords-chart'
						className='minimal'
						onChange={e => {
							setSelectedChordSuffix(e.target.value);
						}}>
						{selectedChordKey === null ? <option>-- izberi lestvico --</option> : chords_keyed[selectedChordKey].sort((a, b) => {
							const ord_a = chord_suffix_order.indexOf(a) === -1 ? 9999 : chord_suffix_order.indexOf(a);
							const ord_b = chord_suffix_order.indexOf(b) === -1 ? 9999 : chord_suffix_order.indexOf(b);
							
							return ord_a - ord_b;
						}).map(key => <option
							key={'key-' + selectedChordKey + '-suffix-' + key}
							value={key}
							checked={key == selectedChordSuffix}>
							{ translateSuffix(key) }
						</option>)}
					</select>
				</div>
			</div>
		</div>
		
		<div className='chords-utility' style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', flexWrap: 'wrap' }}>
			{chord_variations[selectedChordKey + '--' + selectedChordSuffix].map((variation, idx) =>
				<div key={'chord-position-' + selectedChordKey + '--' + selectedChordSuffix + '--' + idx}>
					<div className={'chord-react' + (instrument === 'piano' ? '' : ' auto-color-scheme')}>
						<ChordShape
							variation={variation}
							instrument={instrument}
							lite={false} />
					</div>
				</div>
			)}
		</div>

	</div>;
};
registerComponent('ChordsUtility', ChordsUtility);

const getInstrument = instrument_type => {
	return {
		strings: instrument_type == 'guitar' ? 6 : 4,
		fretsOnChord: 4,
		name: instrument_type == 'guitar' ? 'Guitar' : 'Ukulele',
		keys: [],
		tunings: {
			standard: instrument_type == 'guitar' ? ['E', 'A', 'D', 'G', 'H', 'E'] : ['G', 'C', 'E', 'A']
		}
	};
};

let tooltipHideTimeout = -1;
let mouseEnterLeaveElement = null;

const resetChordPositions = (node, state, transpose_key, showTooltip, hideTooltip) => {
	if (node === null || node === undefined) return;
	
	//const shift = state.get().shift % chord_order.length;
	
	const chords = node.querySelectorAll('.lyric-chord');
	
	chords.forEach(chord => {
		const chord_position_el = node.querySelector('.lyric-chord-position[data-offset="' + chord.dataset.offset + '"]');
		
		let chord_left = chord_position_el.offsetLeft;
		let chord_top  = chord_position_el.offsetTop;
		
		const c = chord.dataset.chord;
		if (chord_db[c] === undefined) return;

		const current_shift = state.get().shift[transpose_key] || 0;
		const chord_entry   = resolveChordEntry(c, state.get().simplify_chords);

		const chord_key         = chord_entry.chord_key;
		const shift             = (chord_order.indexOf(chord_key) + current_shift) % chord_order.length;
		const chord_key_shifted = chord_order[shift] + chord_entry.chord_suffix;

		const chord_key_2         = chord_entry.chord_key_2;
		const shift_2             = chord_key_2.length == 0 ? 0 : ((chord_order.indexOf(chord_key_2) + current_shift) % chord_order.length);
		const chord_key_2_shifted = (chord_key_2.length == 0 ? '' : chord_order[shift_2]) + chord_entry.chord_suffix_2;
		
		let chord_title = chord_key_shifted;
		if (chord_key_2_shifted.length > 0) {
			chord_title += '/' + chord_key_2_shifted;
		}
		
		if (chord.dataset.inline === 'false') {
			chord.style.position = 'absolute';
			//chord.style.left     = chord_left + 'px';
			//chord.style.top      = (chord_top) + 'px'; // - (parseInt(state.get().font_size) * 2)) + 'px';
			chord.style.transform = 'translateY(-35%)';
		}
		chord.innerHTML = chord_title;
		chord.classList.add('processed');
		
		if (showTooltip !== undefined) {
			chord.addEventListener('mouseenter', () => showTooltip(chord.dataset.offset, 'chord', chord_title));
			chord.addEventListener('click',      () => showTooltip(chord.dataset.offset, 'chord', chord_title, true));
		}
		if (hideTooltip !== undefined) {
			chord.addEventListener('mouseleave', () => hideTooltip(chord.dataset.offset, 'chord'));
		}
	});
	
	for (let i=1; i<chords.length; i++) {
		// only compare chords on the same line
		if (Math.abs(parseInt(chords[i].style.top) - parseInt(chords[i - 1].style.top)) > 5) continue;
		
		if (parseInt(chords[i].style.left) <= (parseInt(chords[i - 1].style.left) + chords[i - 1].getBoundingClientRect().width)) {
			chords[i].style.left = (parseInt(chords[i - 1].style.left) + chords[i - 1].getBoundingClientRect().width + 8) + 'px';
		}
	}
};

const Lyrics = props => {
	const state = useHookstate(globalState);
	
	const [ tooltipOpenIdx,  setTooltipOpenIdx  ] = useState(-1);
	const [ tooltipOpenKey,  setTooltipOpenKey  ] = useState(null);
	const [ slideIdx,        setSlideIdx        ] = useState(0);
	const [ fontSize,        setFontSize        ] = useState(state.get().font_size);
	const [ columnCount,     setColumnCount     ] = useState(state.get().column_count);
	const [ domNode,         setDomNode         ] = useState(null);
	const [ shift,           setShift           ] = useState(JSON.stringify(state.get().shift));
	const [ simplify,        setSimplify        ] = useState(state.get().simplify_chords);
	const [ tooltipIsSticky, setTooltipIsSticky ] = useState(false);
	
	const tooltipIsStickyRef = useRef(tooltipIsSticky);
	tooltipIsStickyRef.current = tooltipIsSticky;
	
	const tooltipOpenIdxRef = useRef(tooltipOpenIdx);
	tooltipOpenIdxRef.current = tooltipOpenIdx;
	
	const enableTooltips = props.enableTooltips === undefined ? true : props.enableTooltips;
	
	const onRefChange = useCallback(node => {
		setDomNode(node);
		resetChordPositions(node, state, props.transpose_key, showTooltip, hideTooltip);
	}, []);
	
	const showTooltip = (idx, el, chord, sticky) => {
		if (!enableTooltips) return;
		
		sticky = sticky || false;
		
		mouseEnterLeaveElement = el;
		setTooltipIsSticky(sticky);
		
		if (tooltipHideTimeout != -1) {
			clearTimeout(tooltipHideTimeout);
			tooltipHideTimeout = -1;
		}
		if (tooltipOpenIdxRef.current != idx) {
			setSlideIdx(0);
		}
		setTooltipOpenIdx(idx);
		setTooltipOpenKey(chord);
	};
	const hideTooltip = (idx, el, force) => {
		force = force || false;
		
		if (!force && tooltipIsStickyRef.current) return;
		
		if (!force && el != mouseEnterLeaveElement) return;
		
		tooltipHideTimeout = setTimeout(() => {
			setTooltipOpenIdx(-1);
			setTooltipOpenKey(null);
			tooltipHideTimeout = -1;
		}, force ? 50 : 500);
	};
	
	if (fontSize != state.get().font_size) {
		setFontSize(state.get().font_size);
	}
	if (shift != JSON.stringify(state.get().shift)) {
		setShift(JSON.stringify(state.get().shift));
	}
	if (columnCount != state.get().column_count) {
		setColumnCount(state.get().column_count);
	}
	if (simplify != state.get().simplify_chords) {
		setSimplify(state.get().simplify_chords);
	}

	useEffect(() => {
		resetChordPositions(domNode, state, props.transpose_key, showTooltip, hideTooltip);
	}, [ fontSize, shift, columnCount, simplify ]);
	
	const column_count = (props.handleColumnCountInCss === undefined || props.handleColumnCountInCss === false) ? 1 : columnCount;
	
	return <div>
		<div
			className='lyric-chords'
			style={{ fontSize: (state.get().font_size + 'em'), columnCount: column_count }}
			ref={onRefChange}>
			{props.lyrics.map((x, idx) => <div key={'line-' + idx} className='chords-lyrics-line' style={{ position: 'relative' }} dangerouslySetInnerHTML={{__html: (x == '' ? '&nbsp;' : x) }} />)}
		</div>
		
		{!state.get().show_chord_tooltip || tooltipOpenIdx == -1 ? null : (() => {
			const chord_positions = getChordVariations(tooltipOpenKey, tooltipOpenKey, props.instrument);

			const lite = false;
			
			return <Tooltip
				anchorId={'tooltip-anchor-' + tooltipOpenIdx}
				className='chord-tooltip'
				isOpen={true}
				noArrow={true}
				offset={0}>
				<div
					className='chord-tooltip-content'
					onMouseEnter={() => {
						if (tooltipHideTimeout != -1) {
							showTooltip(tooltipOpenIdx, 'tooltip', tooltipOpenKey);
						}
					}}
					onMouseLeave={() => hideTooltip(tooltipOpenIdx, 'tooltip')}>
					<Slider dots={false} infinite={true} slidesToShow={1} arrows={true} beforeChange={(oldIdx, newIdx) => {
						setSlideIdx(newIdx);
					}}>
						{chord_positions.map((variation, idx) =>
							<div className='chord-react' key={'chord-' + tooltipOpenKey + '-pos-' + idx}>
								<ChordShape
									variation={variation}
									instrument={props.instrument}
									lite={lite} />
							</div>
						)}
					</Slider>
					<div style={{ textAlign: 'center', color: '#000000' }}>
						{ slideIdx + 1 } / { chord_positions.length }
					</div>
				</div>
				{!tooltipIsSticky ? null :
					<button className='chord-tooltip-close' onClick={() => {
						hideTooltip(tooltipOpenIdx, 'tooltip', true);
					}}>×</button>
				}
			</Tooltip>;
		})()}
	</div>;
};
registerComponent('Lyrics', Lyrics);

let sheet_height = -1;

// from https://stackoverflow.com/a/29881817/1612645
const getElementHeight = element => {
	const computed_style = getComputedStyle(element);
	
	element_height = element.clientHeight;
	element_width  = element.clientWidth;
	
	// subtract padding
	element_height -= parseFloat(computed_style.paddingTop ) + parseFloat(computed_style.paddingBottom);
	element_width  -= parseFloat(computed_style.paddingLeft) + parseFloat(computed_style.paddingRight );
	
	return element_height;
};

const AdvancedPrint = props => {
	const state = useHookstate(globalState);
	
	const [ fontSize,    setFontSize    ] = useState(state.get().font_size);
	const [ columnCount, setColumnCount ] = useState(state.get().column_count);
	const [ showChords,  setShowChords  ] = useState(state.get().show_chords);
	const [ simplify,    setSimplify    ] = useState(state.get().simplify_chords);
	const [ domNode,     setDomNode     ] = useState(null);
	const [ pages,       setPages       ] = useState([]);
	
	const onRefChange = useCallback(node => {
		setDomNode(node);
	}, []);
	
	if (fontSize != state.get().font_size) {
		setFontSize(state.get().font_size);
	}
	if (columnCount != state.get().column_count) {
		setColumnCount(state.get().column_count);
	}
	if (showChords != state.get().show_chords) {
		setShowChords(state.get().show_chords);
	}
	if (simplify != state.get().simplify_chords) {
		setSimplify(state.get().simplify_chords);
	}

	useEffect(() => {
		if (domNode === null) return;
		
		const offstage = domNode.getElementsByClassName('chords-lyrics-offstage');
		if (offstage.length == 0) return;
		
		const lines = offstage[0].getElementsByClassName('chords-lyrics-line');
		console.log({ lines });
		
		const two_columns       = columnCount == 2;
		const lyrics_containers = domNode.getElementsByClassName('song-lyrics');
		const lyrics_container  = lyrics_containers[0];
		const sheets            = domNode.getElementsByClassName('sheet');
		const sheet             = sheets[0];
		
		if (sheet_height == -1) {
			sheet_height = getElementHeight(sheet);
		}
		
		const first_lyrics_container_height      = (sheet_height - lyrics_container.offsetTop);
		const additional_lyrics_container_height = sheet_height;
		
		const pages = [ [] ];
		
		let height_1 = 0;
		let height_2 = 0;
		
		for (let i=0; i<lines.length; i++) {
			const line_height = getElementHeight(lines[i]);
			
			const single_container_height = (pages.length == 1 ? first_lyrics_container_height : additional_lyrics_container_height)
				- 20 /* allow for some spacing below lyrics */;
			const container_height = single_container_height
				*
				(two_columns ? 2 : 1 /* we have twice the available height with two columns */);
			
			if (height_1 + line_height > single_container_height) {
				height_2 += line_height;
				height_1 = single_container_height;
			}
			else {
				height_1 += line_height;
			}
			
			console.log({
				single_container_height,
				container_height,
				height_1,
				height_2,
				height: height_1 + height_2,
				line_height,
				i,
			});
			if ((height_1 + height_2) > container_height) {
				pages.push([]);
				height_1 = line_height;
				height_2 = 0;
			}
			
			console.log('Adding to page ' + (pages.length - 1));
			pages[pages.length - 1].push(props.lyrics[i]);
		}
		
		setPages(pages);

	}, [ domNode, fontSize, columnCount, showChords ]);
	
	useEffect(() => {
		resetChordPositions(domNode, state, props.transpose_key);
	}, [ pages, simplify ]);
	
	console.log({
		fontSize,
		columnCount,
		showChords,
	});
	
	return <div className='advanced_print' style={{ position: 'relative' }} ref={onRefChange}>
		<div style={{ position: 'absolute', 'width': (columnCount == 1 ? '19cm' : '9.3cm'), 'left': /*'25cm'*/ '-10000px' }} className='chords-lyrics-offstage'>
			<Lyrics
				lyrics={props.lyrics}
				offsets={props.offsets}
				instrument={props.instrument}
				enableTooltips={props.enableTooltips}
				transpose_key={props.transpose_key} />
		</div>
		
		<div className='left sheets'>
			<div className='sheet first-sheet padding-10mm'>
				<div className='head'>
					<h2>{props.artist_title + ' - ' + props.item_title}</h2>
					<div id='print_logo'>
						<div className='logo-main'>
							<span>{ props.trans.site_title }</span>
							<img src={props.qr_code_path} style={{ width: '52px' }} />
						</div>
					</div>
				</div>
				<div className='chords'>
					<Chords
						chords={props.chords}
						instrument={props.instrument}
						transpose_key={props.transpose_key} />
				</div>
				<div className='song'>
					<div className='song-lyrics'>{ pages.length == 0 ? null : <div className='lyric-chords' style={{ fontSize: fontSize + 'em', columns: columnCount }}>
						{ pages[0].map((x, idx) => <div key={'first-sheet-line-' + idx} className='chords-lyrics-line' style={{ position: 'relative' }} dangerouslySetInnerHTML={{__html: (x == '' ? '&nbsp;' : x) }} />) }
					</div> }</div>
				</div>
			</div>
			
			{ pages.length <= 1 ? null : new Array(pages.length - 1).fill(0).map((x, i) => i + 1).map(i => {
				return <div key={'sheet-' + i} className='sheet additional-sheet padding-10mm'>
					<div className='song'>
						<div className='song-lyrics'><div className='lyric-chords' style={{ fontSize: fontSize + 'em', columns: columnCount }}>{ pages[i].map((x, idx) =>
							<div key={'sheet-' + i + ' -line-' + idx} className='chords-lyrics-line' style={{ position: 'relative' }} dangerouslySetInnerHTML={{__html: (x == '' ? '&nbsp;' : x) }} />
						) }</div></div>
					</div>
				</div>;
			}) }
		</div>
		
		<div className='right'>
			<div className='actions'>
				<div className='print-action-row'>
					<div>{ props.trans.chords }</div>
					<div><ChordsToggle style='print' trans={{ chords_on_top: props.trans.chords_on_top, chords: props.trans.chords }} /></div>
				</div>
				<div className='print-action-row'>
					<div>{ props.trans.size }</div>
					<div><FontSize style='print' /></div>
				</div>
				<div className='print-action-row'>
					<div>{ props.trans.two_columns }</div>
					<div><ColumnCountToggle style='print' trans={{ two_columns: props.trans.two_columns }} /></div>
				</div>
			</div>
			<div style={{ position: 'sticky', top: '170px' }}>
				<button className='fancy-button bg-gradient2' onClick={() => window.print()}><span>{ props.trans.print }</span></button>
			</div>
		</div>
	</div>;
};
registerComponent('AdvancedPrint', AdvancedPrint);

registerComponent('SocialShareButton', props => {
	return <ShareOnSocial
		textToShare={props.description}
		link={props.link}
		linkTitle={props.link_title}
		linkMetaDesc={props.description}
		linkFavicon={props.favicon}
		closeText={props.close_text}
		copyToClipboardText={props.copy_clipboard_text}
		copySuccessText={props.copy_success_text}
		noReferer>
		<div dangerouslySetInnerHTML={{ __html: props.element }} />
	</ShareOnSocial>;
});

const DownloadChords = props => {
	const current_shift = 0;
	
	const chords = [
		'C', 'Cm', 'C7', 'Cm7',
		'D', 'Dm', 'D7', 'Dm7',
		'E', 'Em', 'E7', 'Em7',
		'F', 'Fm', 'F7', 'Fm7',
		'G', 'Gm', 'G7', 'Gm7',
		'A', 'Am', 'A7', 'Am7',
		'B', 'Bm', 'B7', 'Bm7',
		'H', 'Hm', 'H7', 'Hm7',
	];
	
	const [ zippedFile, setZippedFile ] = useState(null);
	useEffect(() => {
		const zip = new JSZip();
		
		for (let i=0; i<chords.length; i++) {
			const c = chords[i];
			
			const chord_key         = chord_db[c].chord_key;
			const shift             = (chord_order.indexOf(chord_key) + current_shift) % chord_order.length;
			const chord_key_shifted = chord_order[shift] + chord_db[c].chord_suffix;
			
			const chord_key_2         = chord_db[c].chord_key_2;
			const shift_2             = chord_key_2.length == 0 ? 0 : ((chord_order.indexOf(chord_key_2) + current_shift) % chord_order.length);
			const chord_key_2_shifted = (chord_key_2.length == 0 ? '' : chord_order[shift_2]) + chord_db[c].chord_suffix_2;
			
			let chord_title = chord_key_shifted;
			if (chord_key_2_shifted.length > 0) {
				chord_title += '/' + chord_key_2_shifted;
			}
			
			const chord_positions = (chord_db[chord_title] ?? chord_db[c]).positions;
			chord_positions.sort((a, b) => {
				let val = a.baseFret - b.baseFret;
				if (val == 0) {
					val = a.id - b.id;
				}
				
				return val;
			});
			const chord = chord_positions[0];
			
			const lite = false;
			
			const contents = renderToString(
				<Chord
					chord={chord}
					instrument={getInstrument(props.instrument)}
					lite={lite} />
			);
			zip.file(chord_title.replaceAll('/', '--') + '.svg', contents);
		}
		
		zip.generateAsync({ type: 'blob' }).then(function (blob) {
			setZippedFile(blob);
		});
	}, []);
	
	if (zippedFile !== null) {
		const url = window.URL.createObjectURL(zippedFile);
		return <div>
			<a download='chords.zip' href={url}>download</a>
		</div>;
	}
	
	return <div>loading</div>;
};
registerComponent('DownloadChords', DownloadChords);

const Capo = props => {
	const state = useHookstate(globalState);
	
	let capo = parseInt(state.get().shift[props.transpose_key] ?? 0, 10);
	capo = 12 - capo;
	capo = capo + parseInt(props.capo);
	if (capo >= 12) capo -= 12;
	
	if (capo == 0) {
		capo = props.capo_without;
	}
	else {
		capo += props.capo_suffixes[capo - 1] ?? '';
	}
	
	return <div>{capo}</div>;
};
registerComponent('Capo', Capo);

registerComponent('StrummingDisplay', StrummingDisplay);
registerComponent('StrummingEditor',  StrummingEditor);

const ChordsStrummingTabs = props => {
	const [ activeTab, setActiveTab ] = useState('chords');

	return <div className='chords-strumming-tabs'>
		<div className='sp-tabs'>
			<button
				className={'sp-tab' + (activeTab === 'chords' ? ' sp-tab-active' : '')}
				onClick={() => setActiveTab('chords')}>
				{props.trans.chords}
			</button>
			<button
				className={'sp-tab' + (activeTab === 'strumming' ? ' sp-tab-active' : '')}
				onClick={() => {
					if (!props.hasStrummingAccess) {
						if (window.$) window.$('#strummingPaywall').modal('show');
						return;
					}
					setActiveTab('strumming');
				}}>
				{props.trans.strumming}{ !props.hasStrummingAccess && featureLockBadge }
			</button>
		</div>

		{activeTab === 'chords'
			? <Chords chords={props.chords} instrument={props.instrument} transpose_key={props.transpose_key} />
			: (props.hasStrummingAccess
				? <StrummingDisplay
					patterns={props.patterns}
					songId={props.song_id}
					trans={{
						empty_state:      props.trans.strumming_empty_state,
						request_button:   props.trans.strumming_request_button,
						request_already:  props.trans.strumming_request_already,
						request_thanks:   props.trans.strumming_request_thanks,
					}} />
				: <div className='strumming-locked'>
					<span className='strumming-locked-icon'><svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><rect x='5' y='11' width='14' height='10' rx='2.5' /><path d='M8 11V7a4 4 0 0 1 8 0v4' /><circle cx='12' cy='15.3' r='1.2' fill='currentColor' stroke='none' /><path d='M12 16.6v1.6' /></svg></span>
					<span>{props.trans.strumming_premium_required}</span>
					<button type='button' className='paywall-plan-cta' onClick={() => { if (window.$) window.$('#strummingPaywall').modal('show'); }}>{props.trans.strumming_unlock_button}</button>
				</div>)}
	</div>;
};
registerComponent('ChordsStrummingTabs', ChordsStrummingTabs);

//////////////////////////////////////////////
// all components should be above this line //
//////////////////////////////////////////////

const refreshComponents = () => {
	document.querySelectorAll('[data-react-component]').forEach(root => {
		const componentId = root.dataset.reactComponent;
		if (componentId == '') return;
		
		const componentJsonProps = root.dataset.reactProps;
		const Component          = getComponentById(componentId);
		const props              = parseJsonProps(componentJsonProps);
		
		const react_root = ReactDOM.createRoot(root);
		
		react_root.render(<Component {...props} />);
		
		root.dataset.reactComponent = '';
		root.dataset.reactProps     = '';
	});
};
refreshComponents();

if (typeof chords_events !== 'undefined') {
	chords_events.addEventListener('refresh', refreshComponents);
}
